import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    Box, Typography, Grid, Button, Stack, Chip, Avatar, IconButton, Tooltip, Collapse, LinearProgress, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Autocomplete, Checkbox, Snackbar, Alert,
} from '@mui/material';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { useSelector, useDispatch } from 'react-redux';
import { selectEmployees, mapApiEmployee } from '../../redux/slices/employeesSlice';
import { selectActiveEntity } from '../../redux/slices/orgSlice';
import useOrgOptions from '../../hooks/useOrgOptions';
import {
    selectDocTypes,
    resetTarget, addDocType,
} from '../../redux/slices/documentsSlice';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_DARK, PRIMARY_BORDER } from '../../theme';
import { fmtDate, initialsFromName as initials, avatarTone } from '../../utils/format';
import { openFile, fmtSize } from '../../utils/fileStore';
import { solidBtn, ghostBtn, field, Panel, EmptyState, StatusChip, FileBadge, ConfirmDialog, SkeletonRows, LoadAlert } from '../uiKit';
import http, { apiErrorMessage } from '../../Api/http';
import { GetDocumentRequests, GetKnownDocumentTypes, PostDocumentRequest, PostAddRecipients, DeleteDocumentRequest, GetEmployees } from '../../Api/Api';

export const CATEGORIES = ['Identity', 'Education', 'Employment', 'Bank', 'Address', 'Medical', 'General'];
// The document types the onboarding form collects, offered alongside the saved ones so
// a request can be raised for any of them with one pick.
export const ONBOARDING_DOCS = [
    { slug: 'aadhaar-card', name: 'Aadhaar Card', category: 'Identity' },
    { slug: 'pan-card', name: 'PAN Card', category: 'Identity' },
    { slug: 'passport', name: 'Passport', category: 'Identity' },
    { slug: 'resume', name: 'Resume', category: 'Employment' },
    { slug: 'educational-certificates', name: 'Educational Certificates', category: 'Education' },
    { slug: 'experience-certificates', name: 'Experience Certificates', category: 'Employment' },
    { slug: 'offer-letter', name: 'Offer Letter', category: 'Employment' },
    { slug: 'appointment-letter', name: 'Appointment Letter', category: 'Employment' },
    { slug: 'relieving-letter', name: 'Relieving Letter', category: 'Employment' },
    { slug: 'bank-passbook', name: 'Bank Passbook / Cancelled Cheque', category: 'Bank' },
    { slug: 'other', name: 'Other Documents', category: 'General' },
].map((d) => ({ id: `known-${d.slug}`, key: d.slug, name: d.name, category: d.category, description: 'Onboarding document' }));

// Types the server still lists but the app no longer offers in its document dropdowns.
export const HIDDEN_DOC_KEYS = ['joining-letter', 'address-proof', 'signature'];

const CUSTOM_DOC = '__custom__';

const EMPTY = { docTypeId: '', docKey: '', title: '', category: 'General', note: '', dueDate: '', mandatory: 'yes', employees: [] };

const todayInput = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fullName = (e) => `${e.firstName || ''} ${e.lastName || ''}`.trim();

const isoFromDMY = (s) => {
    const m = String(s || '').match(/^(\d{2})-(\d{2})-(\d{4})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : (s || '');
};

const dmyFromIso = (s) => {
    const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : (s || '');
};

const normalizeDocRequest = (it) => {
    const total = Number(it.totalRecipients) || (Array.isArray(it.recipients) ? it.recipients.length : 0);
    const approved = Number(it.approved) || 0;
    return {
        id: it.id,
        title: it.documentName ?? '',
        category: it.category ?? '',
        status: String(it.status ?? '').toLowerCase(),
        mandatory: Boolean(it.isMandatory),
        requestedBy: it.requestedByName ?? '',
        requestedOn: isoFromDMY(it.requestedOn),
        dueDate: isoFromDMY(it.dueDate),
        entityId: it.entityId,
        entityName: it.entityName ?? '',
        note: it.note ?? '',
        progress: {
            total,
            approved,
            submitted: Number(it.toReview) || 0,
            pending: Number(it.pending) || 0,
            rejected: Number(it.rejected) || 0,
            pct: total ? Math.round((approved / total) * 100) : 0,
        },
        targets: (Array.isArray(it.recipients) ? it.recipients : []).map((r) => ({
            recipientId: r.recipientId,
            employeeId: r.employeeCode ?? '',
            employeeName: r.employeeName ?? '',
            department: '',
            status: String(r.status ?? '').toLowerCase(),
        })),
    };
};

export default function DocumentRequestsTab() {
    const dispatch = useDispatch();
    const docTypes = useSelector(selectDocTypes);
    const storeEmployees = useSelector(selectEmployees);
    const { departments } = useOrgOptions();
    // The shared store only holds the one page the Employees screen last showed (20 people), so the request dialog loads
    // every employee itself each time it opens. Until that arrives, or if it fails, the store's list is used.
    const [allEmployees, setAllEmployees] = useState(null);
    const [employeesLoading, setEmployeesLoading] = useState(false);
    const loadAllEmployees = useCallback(async () => {
        setEmployeesLoading(true);
        try {
            const { data: body } = await http.get(GetEmployees, { params: { search: '', status: 'active', page: 1, pageSize: 1000 } });
            const items = Array.isArray(body?.data?.items) ? body.data.items : [];
            if (!body?.error && items.length) setAllEmployees(items.map(mapApiEmployee).filter((e) => e.status === 'Active'));
        } catch { /* keep whatever list is already there */ } finally { setEmployeesLoading(false); }
    }, []);
    const employees = allEmployees || storeEmployees;
    const activeEntity = useSelector(selectActiveEntity);

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const loadRequests = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetDocumentRequests);
            if (body?.error) throw new Error(body.message || 'Could not load document requests.');
            const items = Array.isArray(body?.data?.items) ? body.data.items : [];
            setRequests(items.map(normalizeDocRequest));
            setLoadError('');
        } catch (err) {
            setLoadError(apiErrorMessage(err, 'Could not load document requests.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadRequests(); }, [loadRequests]);

    const [open, setOpen] = useState({});
    const [dialog, setDialog] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [tried, setTried] = useState(false);
    const [docCustom, setDocCustom] = useState(false);
    // The fixed onboarding document types from the server ({ key, label }); the built-in list
    // stands in until it arrives (or if the call fails).
    const [knownTypes, setKnownTypes] = useState(() => ONBOARDING_DOCS.map((d) => ({ key: d.key, label: d.name })));
    useEffect(() => {
        let off = false;
        http.get(GetKnownDocumentTypes).then(({ data: body }) => {
            const d = body?.data;
            const list = Array.isArray(d) ? d : (d?.types || d?.documentTypes || d?.items || []);
            if (!off && !body?.error && list.length) setKnownTypes(list.filter((t) => t?.key && !HIDDEN_DOC_KEYS.includes(t.key)).map((t) => ({ key: t.key, label: t.label || t.key })));
        }).catch(() => { /* keep the built-in list */ });
        return () => { off = true; };
    }, []);
    const labelOfDoc = (name) => knownTypes.find((k) => k.key === name)?.label || name;
    const [confirm, setConfirm] = useState(null);
    const [snack, setSnack] = useState(null);

    const notify = (msg, sev = 'success') => setSnack({ msg, sev });
    const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

    // Known onboarding types first (they carry the key the server matches on), then any saved
    // document types that aren't one of them.
    const docOptions = useMemo(() => {
        const known = knownTypes.map((k) => ({
            id: `known-${k.key}`, key: k.key, name: k.label,
            category: ONBOARDING_DOCS.find((o) => o.key === k.key)?.category || 'General',
            description: 'Onboarding document',
        }));
        const rest = docTypes.filter((t) => !known.some((o) => o.name.toLowerCase() === String(t.name || '').trim().toLowerCase()));
        return [...known, ...rest];
    }, [docTypes, knownTypes]);

    // Requests are stored under the known type's key ("pan-card"); show its label.
    const requestsView = useMemo(() => requests.map((r) => ({ ...r, title: labelOfDoc(r.title) })), [requests, knownTypes]);

    const options = useMemo(() => employees.map((e) => ({
        employeeId: e.employeeId,
        employeeName: fullName(e),
        department: e.department || '',
    })), [employees]);

    const openCreate = () => { loadAllEmployees(); setForm(EMPTY); setTried(false); setDocCustom(false); setDialog({ mode: 'create' }); };
    const openAddPeople = (req) => {
        loadAllEmployees();
        setForm({ ...EMPTY, title: req.title, employees: [] });
        setTried(false);
        setDialog({ mode: 'add', requestId: req.id, existing: req.targets.map((t) => t.employeeId) });
    };

    const pickDocType = (_, v) => {
        if (!v) { setForm((f) => ({ ...f, docTypeId: '', docKey: '', title: '' })); return; }
        if (typeof v === 'string') { setForm((f) => ({ ...f, docTypeId: '', docKey: '', title: v })); return; }
        setForm((f) => ({ ...f, docTypeId: v.id, docKey: v.key || '', title: v.name, category: v.category || f.category }));
    };

    const addableOptions = dialog?.mode === 'add'
        ? options.filter((o) => !dialog.existing.includes(o.employeeId))
        : options;

    const selectDepartment = (depName) => {
        const inDep = addableOptions.filter((o) => o.department === depName);
        setForm((f) => {
            const have = new Set(f.employees.map((e) => e.employeeId));
            return { ...f, employees: [...f.employees, ...inDep.filter((o) => !have.has(o.employeeId))] };
        });
    };

    const today = todayInput();
    const dueInPast = Boolean(form.dueDate) && form.dueDate < today;
    const formValid = form.title.trim() && form.employees.length > 0 && !dueInPast;

    const submitRequest = async () => {
        setTried(true);
        if (!formValid) {
            notify(!form.title.trim() ? 'Give the document a name.'
                : dueInPast ? 'The due date can’t be in the past — pick today or a later date.'
                    : 'Pick at least one employee to request it from.', 'warning');
            return;
        }

        const codes = form.employees.map((e) => e.employeeId);
        setSaving(true);
        try {
            if (dialog.mode === 'add') {
                const { data: body } = await http.post(PostAddRecipients, {
                    requestId: dialog.requestId,
                    employeeCodes: codes,
                });
                if (body?.error) throw new Error(body.message || 'Could not add recipients.');
                const skipped = Number(body?.data?.skippedAlreadyOnFile?.length ?? body?.data?.skippedAlreadyOnFile ?? body?.skippedAlreadyOnFile ?? 0) || 0;
                notify(body?.message || `Requested from ${codes.length - skipped} more employee${codes.length - skipped === 1 ? '' : 's'}.`, skipped > 0 ? 'warning' : 'success');
            } else {
                if (!form.docTypeId && !docOptions.some((t) => t.name.trim().toLowerCase() === form.title.trim().toLowerCase())) {
                    dispatch(addDocType({ name: form.title.trim(), category: form.category }));
                }
                const { data: body } = await http.post(PostDocumentRequest, {
                    // A known type goes by its key so the server can skip anyone who already has it.
                    documentName: form.docKey || form.title.trim(),
                    category: form.category,
                    dueDate: dmyFromIso(form.dueDate),
                    isMandatory: form.mandatory === 'yes',
                    note: form.note,
                    employeeCodes: codes,
                    entityId: null,
                    entityName: activeEntity?.name || '',
                });
                if (body?.error) throw new Error(body.message || 'Could not create the request.');
                const skipped = Number(body?.data?.skippedAlreadyOnFile?.length ?? body?.data?.skippedAlreadyOnFile ?? body?.skippedAlreadyOnFile ?? 0) || 0;
                notify(body?.message || `${form.title.trim()} requested from ${codes.length - skipped} employee${codes.length - skipped === 1 ? '' : 's'}.`, skipped > 0 ? 'warning' : 'success');
            }
            setDialog(null);
            await loadRequests();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not save the request.'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const doCancel = async () => {
        if (!confirm || cancelling) return;
        setCancelling(true);
        try {
            const { data: body } = await http.delete(DeleteDocumentRequest, { params: { id: confirm.id } });
            if (body?.error) throw new Error(body.message || 'Could not cancel the request.');
            notify(body?.message || `${confirm.title} request cancelled.`);
            setConfirm(null);
            await loadRequests();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not cancel the request.'), 'error');
        } finally {
            setCancelling(false);
        }
    };

    const view = (target) => {
        if (!openFile(target.fileKey)) notify('That file was uploaded in an earlier session, so there is nothing to preview here.', 'warning');
    };

    return (
        <Box>
            <Panel
                title="Document Requests"
                icon={AssignmentRoundedIcon}
                chip={`${requestsView.filter((r) => r.status === 'open').length} open`}
                chipColor="#B45309"
                chipBg="#FFF7ED"
                action={(
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Tooltip arrow title="Reload">
                            <IconButton onClick={loadRequests} disabled={loading} sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', color: '#64748B', '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        <Button startIcon={<AddRoundedIcon />} onClick={openCreate} sx={{ ...solidBtn, height: 38, px: 1.8, fontSize: 13 }}>New Request</Button>
                    </Stack>
                )}
            >
                {loading ? (
                    <SkeletonRows count={5} />
                ) : loadError ? (
                    <Box sx={{ p: 3 }}>
                        <LoadAlert sx={{ borderRadius: '9px' }}>
                            {loadError}
                        </LoadAlert>
                    </Box>
                ) : requestsView.length === 0 ? (
                    <EmptyState icon={AssignmentRoundedIcon} title="No document requests yet" hint="Name a document once — e.g. Birth Certificate — and request it from as many employees as you need." />
                ) : (
                    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                        {requestsView.map((req) => {
                            const p = req.progress;
                            const isOpen = Boolean(open[req.id]);
                            const overdue = (() => {
                                if (!req.dueDate || req.status !== 'open') return false;
                                const due = new Date(req.dueDate);
                                if (Number.isNaN(due.getTime())) return false;
                                const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
                                const now = new Date();
                                const todayDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                return dueDay < todayDay;
                            })();
                            return (
                                <Box key={req.id} sx={{ border: '1px solid #EEF0F6', borderRadius: '9px', overflow: 'hidden', bgcolor: '#fff' }}>
                                    {/* Grid, not flex: the avatar cluster is a different width on every
                                        row (one person or five), which shunted the progress column to a
                                        different position each time. Fixed tracks keep it in a line. */}
                                    <Box
                                        onClick={() => toggle(req.id)}
                                        sx={{
                                            p: 1.8, cursor: 'pointer', alignItems: 'center', gap: 1.6,
                                            display: 'grid',
                                            gridTemplateColumns: {
                                                xs: '42px minmax(0, 1fr) auto',
                                                md: '42px minmax(0, 1fr) 260px 116px auto',
                                            },
                                            '&:hover': { bgcolor: '#FBFAFE' },
                                        }}
                                    >
                                        <Box sx={{ width: 42, height: 42, borderRadius: '9px', bgcolor: PRIMARY_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <AssignmentRoundedIcon sx={{ fontSize: 21, color: PRIMARY }} />
                                        </Box>

                                        <Box sx={{ minWidth: 0 }}>
                                            <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                                <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: '#0F172A' }}>{req.title}</Typography>
                                                <StatusChip status={req.status} />
                                                {/* Mandatory is on most rows, so it stays quiet — red is reserved
                                                    for Overdue, which is the one that actually needs chasing. */}
                                                {req.mandatory && <Chip label="Mandatory" size="small" sx={{ height: 19, fontSize: 9.5, fontWeight: 700, bgcolor: '#F5F7FA', color: '#64748B', border: '1px solid #E6EAF1' }} />}
                                                {overdue && <Chip label="Overdue" size="small" sx={{ height: 19, fontSize: 9.5, fontWeight: 800, bgcolor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }} />}
                                            </Stack>
                                            <Typography sx={{ fontSize: 11.5, color: '#98A0AE', mt: 0.2 }}>
                                                Requested by {req.requestedBy} on {fmtDate(req.requestedOn)}
                                                {req.dueDate ? ` · due ${fmtDate(req.dueDate)}` : ''}
                                            </Typography>
                                        </Box>

                                        {/* Bounded rather than flexible: a progress bar stretched across a
                                            wide row reads as a gap between the title and the people on it. */}
                                        <Box sx={{ gridColumn: { xs: '2 / 4', md: 'auto' }, minWidth: 0 }}>
                                            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 0.6 }}>
                                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: p.pct === 100 ? '#15803D' : '#334155', whiteSpace: 'nowrap' }}>
                                                    {p.approved} of {p.total} approved
                                                </Typography>
                                                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#98A0AE', fontVariantNumeric: 'tabular-nums' }}>
                                                    {p.pct}%
                                                </Typography>
                                            </Stack>
                                            <LinearProgress
                                                variant="determinate" value={p.pct}
                                                sx={{
                                                    height: 6, borderRadius: 5, bgcolor: '#EEF0F6',
                                                    '& .MuiLinearProgress-bar': { bgcolor: p.pct === 100 ? '#22A45D' : PRIMARY, borderRadius: 5 },
                                                }}
                                            />
                                            {/* One line for the outstanding work — "to review" used to print
                                                here and again above the bar. */}
                                            {/* Reserved height so a row with nothing outstanding is the same
                                                height as one with a pending count. */}
                                            <Stack direction="row" spacing={1.2} sx={{ mt: 0.6, flexWrap: 'wrap', minHeight: 17 }}>
                                                {[
                                                    { l: 'pending', v: p.pending, c: '#B45309' },
                                                    { l: 'to review', v: p.submitted, c: '#0369A1' },
                                                    { l: 'rejected', v: p.rejected, c: '#E11D48' },
                                                ].filter((x) => x.v > 0).map((x) => (
                                                    <Stack key={x.l} direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                                                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: x.c }} />
                                                        <Typography sx={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>{x.v} {x.l}</Typography>
                                                    </Stack>
                                                ))}
                                                {p.pct === 100 && (
                                                    <Typography sx={{ fontSize: 11, color: '#15803D', fontWeight: 700 }}>All in</Typography>
                                                )}
                                            </Stack>
                                        </Box>

                                        <Stack direction="row" sx={{ alignItems: 'center', gridColumn: { xs: '2 / 4', md: 'auto' } }}>
                                            {req.targets.slice(0, 4).map((t, i) => (
                                                <Tooltip arrow key={t.employeeId} title={`${t.employeeName} · ${t.status}`}>
                                                    <Avatar sx={{ width: 30, height: 30, ...avatarTone(t.employeeName), fontSize: 11, fontWeight: 700, border: '2px solid #fff', ml: i ? '-8px' : 0 }}>{initials(t.employeeName)}</Avatar>
                                                </Tooltip>
                                            ))}
                                            {req.targets.length > 4 && (
                                                <Avatar sx={{ width: 30, height: 30, bgcolor: '#EEF0F6', color: '#64748B', fontSize: 10.5, fontWeight: 800, border: '2px solid #fff', ml: '-8px' }}>+{req.targets.length - 4}</Avatar>
                                            )}
                                        </Stack>

                                        <Stack
                                            direction="row" spacing={0.3}
                                            sx={{ alignItems: 'center', justifyContent: 'flex-end', gridColumn: { xs: 3, md: 'auto' }, gridRow: { xs: 1, md: 'auto' } }}
                                        >
                                            <Tooltip arrow title="Request from more employees">
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); openAddPeople(req); }} sx={{ color: '#94A3B8', '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT } }}>
                                                    <PersonAddAlt1RoundedIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip arrow title="Cancel request">
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setConfirm(req); }} sx={{ color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}>
                                                    <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <ExpandMoreRoundedIcon sx={{ fontSize: 22, color: '#94A3B8', transition: 'transform .2s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                                        </Stack>
                                    </Box>

                                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                        {req.note && (
                                            <Box sx={{ mx: 1.8, mb: 1.2, p: 1.4, borderRadius: '7px', bgcolor: '#FFFBEB', border: '1px solid #FDE68A' }}>
                                                <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#B45309', textTransform: 'uppercase', letterSpacing: 0.5 }}>Note to employees</Typography>
                                                <Typography sx={{ fontSize: 12.5, color: '#78350F', mt: 0.2 }}>{req.note}</Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ borderTop: '1px solid #EEF0F6', bgcolor: '#FBFAFE' }}>
                                            {req.targets.map((t) => (
                                                <Stack key={t.employeeId} direction="row" sx={{ alignItems: 'center', gap: 1.5, px: 1.8, py: 1.4, borderBottom: '1px solid #F1F0F9', flexWrap: 'wrap' }}>
                                                    <Avatar sx={{ width: 36, height: 36, ...avatarTone(t.employeeName), fontSize: 12.5, fontWeight: 700 }}>{initials(t.employeeName)}</Avatar>
                                                    <Box sx={{ minWidth: 150 }}>
                                                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{t.employeeName}</Typography>
                                                        <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>{t.employeeId}{t.department ? ` · ${t.department}` : ''}</Typography>
                                                    </Box>

                                                    <Box sx={{ minWidth: 130 }}><StatusChip status={t.status} /></Box>

                                                    <Box sx={{ flex: 1, minWidth: 200 }}>
                                                        {t.fileName ? (
                                                            <>
                                                                <FileBadge fileName={t.fileName} fileSize={t.fileSize} sub={`${fmtSize(t.fileSize)} · submitted ${fmtDate(t.submittedOn)}`} />
                                                                {t.remark && <Typography sx={{ fontSize: 11.5, color: '#64748B', mt: 0.4, fontStyle: 'italic' }}>“{t.remark}”</Typography>}
                                                            </>
                                                        ) : t.status === 'rejected' ? (
                                                            <Typography sx={{ fontSize: 12, color: '#E11D48' }}>Rejected{t.reason ? ` — ${t.reason}` : ''}</Typography>
                                                        ) : t.status === 'submitted' ? (
                                                            <Typography sx={{ fontSize: 12, color: '#0369A1', fontWeight: 600 }}>Submitted — awaiting your review</Typography>
                                                        ) : t.status === 'approved' ? (
                                                            <Typography sx={{ fontSize: 12, color: '#16A34A', fontWeight: 600 }}>Approved · filed in their record</Typography>
                                                        ) : (
                                                            <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center' }}>
                                                                <PhoneIphoneRoundedIcon sx={{ fontSize: 14, color: '#CBD2DD' }} />
                                                                <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>Waiting for the employee to upload from the app</Typography>
                                                            </Stack>
                                                        )}
                                                        {t.status === 'approved' && t.reviewedBy && (
                                                            <Typography sx={{ fontSize: 11, color: '#16A34A', mt: 0.3, fontWeight: 600 }}>Approved by {t.reviewedBy} on {fmtDate(t.reviewedOn)} · filed in their record</Typography>
                                                        )}
                                                    </Box>

                                                    <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', gap: 0.8 }}>
                                                        {t.fileName && (
                                                            <Tooltip arrow title="View file">
                                                                <IconButton size="small" onClick={() => view(t)} sx={{ color: '#94A3B8', border: '1px solid #E6EAF1', borderRadius: '7px', '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT } }}>
                                                                    <VisibilityRoundedIcon sx={{ fontSize: 17 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {t.status === 'rejected' && (
                                                            <Button onClick={() => dispatch(resetTarget({ requestId: req.id, employeeId: t.employeeId }))} startIcon={<ReplayRoundedIcon sx={{ fontSize: 16 }} />} sx={{ ...ghostBtn, height: 32, px: 1.4, fontSize: 11.5 }}>Ask again</Button>
                                                        )}
                                                    </Stack>
                                                </Stack>
                                            ))}
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Panel>

            <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>
                        {dialog?.mode === 'add' ? `Request ${form.title} from more employees` : 'Request a Document'}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>
                        {dialog?.mode === 'add'
                            ? 'The people you add are asked for the same document under this request.'
                            : 'Name the document once, then pick everyone it should be collected from. It appears in each employee’s app.'}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    {dialog?.mode === 'create' && (
                        <Grid container spacing={1.8} sx={{ mb: 1.8 }}>
                            <Grid size={{ xs: 12, sm: 7 }}>
                                {/* A real dropdown of every onboarding document plus the saved ones.
                                    "Other (type a name)" reveals a text box for a name that isn't listed. */}
                                <TextField
                                    select label="Document name" size="small" fullWidth sx={field}
                                    value={docCustom ? CUSTOM_DOC : (docOptions.find((o) => o.id === form.docTypeId)?.id || '')}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === CUSTOM_DOC) { setDocCustom(true); setForm((f) => ({ ...f, docTypeId: '', docKey: '', title: '' })); return; }
                                        setDocCustom(false);
                                        pickDocType(null, docOptions.find((o) => o.id === v) || null);
                                    }}
                                    error={tried && !form.title.trim()}
                                    helperText={tried && !form.title.trim() ? 'Pick a document or enter a name' : 'Pick a document from the list'}
                                    slotProps={{ select: { MenuProps: { slotProps: { paper: { sx: { maxHeight: 320 } } } } } }}
                                >
                                    {docOptions.map((o) => (
                                        <MenuItem key={o.id} value={o.id} sx={{ display: 'block', py: 0.8 }}>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{o.name}</Typography>
                                            <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{o.category}{o.description ? ` · ${o.description}` : ''}</Typography>
                                        </MenuItem>
                                    ))}
                                    <MenuItem value={CUSTOM_DOC} sx={{ fontSize: 13.5, fontWeight: 700, color: '#7C5CFC', borderTop: '1px solid #EEF0F6' }}>Other (type a name)…</MenuItem>
                                </TextField>
                                {docCustom && (
                                    <TextField
                                        label="Document name" size="small" fullWidth autoFocus sx={{ ...field, mt: 1.4 }}
                                        value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value, docTypeId: '', docKey: '' }))}
                                        error={tried && !form.title.trim()} placeholder="e.g. Vaccination Certificate"
                                    />
                                )}
                            </Grid>
                            <Grid size={{ xs: 12, sm: 5 }}>
                                <TextField select label="Category" size="small" fullWidth value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} sx={field}>
                                    {CATEGORIES.map((c) => <MenuItem key={c} value={c} sx={{ fontSize: 13.5 }}>{c}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField label="Due date" type="date" size="small" fullWidth value={form.dueDate}
                                    onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
                                    error={dueInPast}
                                    helperText={dueInPast ? 'Due date can’t be in the past' : ' '}
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: today } }} sx={field} />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField select label="Mandatory?" size="small" fullWidth value={form.mandatory} onChange={(e) => setForm((f) => ({ ...f, mandatory: e.target.value }))} sx={field}>
                                    <MenuItem value="yes" sx={{ fontSize: 13.5 }}>Yes — must be submitted</MenuItem>
                                    <MenuItem value="no" sx={{ fontSize: 13.5 }}>No — optional</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid size={12}>
                                <TextField label="Note to employees (optional)" size="small" fullWidth multiline minRows={2}
                                    placeholder="e.g. Please upload a clear scan of the original."
                                    value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} sx={field} />
                            </Grid>
                        </Grid>
                    )}

                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#374151', mb: 0.8 }}>Request from</Typography>
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={addableOptions}
                        loading={employeesLoading}
                        loadingText="Loading employees…"
                        value={form.employees}
                        onChange={(_, v) => setForm((f) => ({ ...f, employees: v }))}
                        getOptionLabel={(o) => `${o.employeeName} (${o.employeeId})`}
                        isOptionEqualToValue={(a, b) => a.employeeId === b.employeeId}
                        renderOption={(props, o, { selected }) => {
                            const { key, ...rest } = props;
                            return (
                                <Box component="li" key={o.employeeId} {...rest}>
                                    <Checkbox checked={selected} size="small" sx={{ mr: 0.5, color: '#CBD2DD', '&.Mui-checked': { color: PRIMARY } }} />
                                    <Avatar sx={{ width: 26, height: 26, ...avatarTone(o.employeeName), fontSize: 10.5, fontWeight: 700, mr: 1.2 }}>{initials(o.employeeName)}</Avatar>
                                    <Box>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{o.employeeName}</Typography>
                                        <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{o.employeeId}{o.department ? ` · ${o.department}` : ''}</Typography>
                                    </Box>
                                </Box>
                            );
                        }}
                        renderTags={(value, getTagProps) =>
                            value.map((o, i) => {
                                const { key, ...rest } = getTagProps({ index: i });
                                return <Chip key={o.employeeId} {...rest} label={o.employeeName} size="small" sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: PRIMARY_LIGHT, color: PRIMARY_DARK, border: `1px solid ${PRIMARY_BORDER}` }} />;
                            })
                        }
                        renderInput={(params) => (
                            <TextField {...params} size="small" placeholder={form.employees.length ? '' : 'Search employees…'}
                                error={tried && form.employees.length === 0} sx={field} />
                        )}
                    />

                    <Stack direction="row" spacing={0.8} sx={{ mt: 1.2, flexWrap: 'wrap', gap: 0.8, alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 11.5, color: '#98A0AE', fontWeight: 600 }}>Quick add:</Typography>
                        <Chip label={employeesLoading ? 'Everyone (loading…)' : `Everyone (${addableOptions.length})`} size="small" disabled={employeesLoading} onClick={() => setForm((f) => ({ ...f, employees: addableOptions }))}
                            sx={{ cursor: 'pointer', fontWeight: 700, fontSize: 11.5, bgcolor: '#F1F5F9', color: '#475569', '&:hover': { bgcolor: '#E2E8F0' } }} />
                        {departments.map((d) => {
                            const n = addableOptions.filter((o) => o.department === d.name).length;
                            if (!n) return null;
                            return (
                                <Chip key={d.id} label={`${d.name} (${n})`} size="small" onClick={() => selectDepartment(d.name)}
                                    sx={{ cursor: 'pointer', fontWeight: 700, fontSize: 11.5, bgcolor: '#F1F5F9', color: '#475569', '&:hover': { bgcolor: '#E2E8F0' } }} />
                            );
                        })}
                        {form.employees.length > 0 && (
                            <Chip label="Clear" size="small" onClick={() => setForm((f) => ({ ...f, employees: [] }))}
                                sx={{ cursor: 'pointer', fontWeight: 700, fontSize: 11.5, bgcolor: '#FEF2F2', color: '#E11D48', '&:hover': { bgcolor: '#FEE2E2' } }} />
                        )}
                    </Stack>

                    {formValid && (
                        <Box sx={{ mt: 2, p: 1.6, borderRadius: '9px', bgcolor: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#15803D', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.4 }}>What happens next</Typography>
                            <Typography sx={{ fontSize: 12.5, color: '#166534' }}>
                                <strong>{form.title.trim()}</strong> is requested from <strong>{form.employees.length} employee{form.employees.length === 1 ? '' : 's'}</strong>.
                                It shows up in their app{form.dueDate ? ` with a due date of ${fmtDate(form.dueDate)}` : ''}; once each one uploads, it lands in <strong>Approvals</strong> for you to review.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDialog(null)} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button onClick={submitRequest} disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <EventRoundedIcon sx={{ fontSize: 18 }} />}
                        sx={{ ...solidBtn, height: 40, px: 2.4 }}>
                        {saving ? 'Saving…' : dialog?.mode === 'add' ? 'Add to request' : 'Send Request'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(confirm)}
                onClose={() => setConfirm(null)}
                onConfirm={doCancel}
                title="Cancel this request?"
                body={confirm ? `The ${confirm.title} request will be withdrawn from all ${confirm.targets.length} employee(s). Files already approved stay in their records.` : ''}
                confirmLabel="Cancel request"
            />

            <Snackbar open={Boolean(snack)} autoHideDuration={3600} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
